import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  offset,
  size as sizing,
  useFloating,
  useDismiss,
  useRole,
  useInteractions,
} from '@floating-ui/react';
import cx from 'classnames';

import { Time } from '../../Icons';

interface TimeInputProps {
  value: string;
  onChange?: (value: string) => void;
  label?: string;
  disabled?: boolean;
}
const TimeInput = ({
  label = 'Time',
  value,
  onChange = () => {},
  disabled,
}: TimeInputProps) => {
  const [open, setOpen] = useState<boolean>(false);

  const { refs, floatingStyles, context } = useFloating({
    placement: 'top-start',
    whileElementsMounted: autoUpdate,
    open,
    onOpenChange: setOpen,
    middleware: [
      offset(1),
      flip({ padding: 10 }),
      sizing({
        apply({ rects, elements }) {
          Object.assign(elements.floating.style, {
            height: '250px',
            minWidth: `${rects.reference.width}px`,
          });
        },
        padding: 5,
      }),
    ],
  });
  const dismiss = useDismiss(context);
  const role = useRole(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([
    dismiss,
    role,
  ]);

  const formattedValue = useMemo(() => {
    const splitTime = value.split(':');
    const hour = parseInt(splitTime[0]);
    const minute = parseInt(splitTime[1]);

    return {
      hour,
      minute,
      isAm: hour < 12,
    };
  }, [value]);

  const hourRef = useRef<HTMLDivElement>(null);
  const minuteRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => {
      if (hourRef?.current) {
        const hour = formattedValue.hour % 12;
        hourRef.current.scrollTop = (hour === 0 ? 12 : hour) * 35 - 240;
      }
      if (minuteRef?.current) {
        minuteRef.current.scrollTop = formattedValue.minute * 35 - 205;
      }
    }, 10);

    return () => {
      clearTimeout(timer);
    };
  }, [open]);

  return (
    <div className="hidden md:inline-flex items-center mb-[10px]">
      <div
        className="time-input-container"
        ref={refs.setReference}
        data-state={open ? 'open' : 'closed'}
        {...getReferenceProps()}
      >
        <label
          htmlFor="time-picker"
          className="text-gray-500 text-[10px] z-[1]"
        >
          {label}
        </label>
        <input
          type="time"
          id="time-picker"
          onChange={(e) => onChange(e.currentTarget.value)}
          value={value}
          disabled={disabled}
          onKeyDown={(e) => {
            if (e.code === 'Space') e.preventDefault();
          }}
        />
        <Time
          width={16}
          height={16}
          className={cx(
            'absolute right-2 top-1/2 -translate-y-1/2',
            disabled
              ? 'cursor-default fill-primary-disabled'
              : 'cursor-pointer fill-black'
          )}
          onClick={() => !disabled && setOpen(true)}
        />

        {open && (
          <FloatingFocusManager context={context} modal={false}>
            <div
              className={cx(
                'inline-flex bg-white border border-black shadow-modal overflow-hidden p-[5px] gap-1'
              )}
              ref={refs.setFloating}
              style={{
                ...floatingStyles,
                zIndex: 999,
              }}
              {...getFloatingProps()}
            >
              <div className="timer-popup-selector" ref={hourRef}>
                {Array.from({ length: 12 }).map((_, index) => (
                  <p
                    key={'hour_' + index}
                    className={cx('cursor-pointer', {
                      'text-white bg-black':
                        index === (formattedValue.hour + 11) % 12,
                    })}
                    onClick={() => {
                      onChange(
                        String(
                          (index + 1 + (formattedValue.isAm ? 0 : 12)) % 24
                        ).padStart(2, '0') +
                          ':' +
                          String(formattedValue.minute).padStart(2, '0')
                      );
                    }}
                  >
                    {index < 9 ? '0' + (index + 1) : index + 1}
                  </p>
                ))}
              </div>
              <div className="timer-popup-selector" ref={minuteRef}>
                {Array.from({ length: 60 }).map((_, index) => (
                  <p
                    key={'minute_' + index}
                    className={cx('cursor-pointer', {
                      'text-white bg-black': index === formattedValue.minute,
                    })}
                    onClick={() => {
                      onChange(
                        String(formattedValue.hour).padStart(2, '0') +
                          ':' +
                          String(index).padStart(2, '0')
                      );
                    }}
                  >
                    {index < 10 ? '0' + index : index}
                  </p>
                ))}
              </div>
            </div>
          </FloatingFocusManager>
        )}
      </div>

      <div className="flex items-center ml-4 h-full">
        <p
          onClick={() => {
            if (!disabled && !formattedValue.isAm) {
              const hour = formattedValue.hour - 12;
              onChange(
                String(hour).padStart(2, '0') +
                  ':' +
                  String(formattedValue.minute).padStart(2, '0')
              );
            }
          }}
          className={cx(
            'w-10 text-[12px] leading-10 text-center cursor-pointer',
            formattedValue.isAm ? 'bg-uta-black text-white' : 'text-uta-black',
            disabled && 'bg-opacity-40 text-opacity-40'
          )}
        >
          AM
        </p>
        <p
          onClick={() => {
            if (!disabled && formattedValue.isAm) {
              onChange(
                String(formattedValue.hour + 12).padStart(2, '0') +
                  ':' +
                  String(formattedValue.minute).padStart(2, '0')
              );
            }
          }}
          className={cx(
            'w-10 text-[12px] leading-10 text-center cursor-pointer',
            formattedValue.isAm ? 'text-uta-black' : 'bg-uta-black text-white',
            disabled && 'bg-opacity-40 text-opacity-40'
          )}
        >
          PM
        </p>
      </div>
    </div>
  );
};

export default TimeInput;
